// Enhanced Health Data Integration for LandingPage
// Maps Oura Ring and iPhone HealthKit data to LandingPage expected format

const HealthDataIntegration = {
  
  /**
   * Transform Azure Function data to LandingPage expected format
   */
  transformForLandingPage(azureFunctionResponse) {
    console.log('🔄 INTEGRATION: Starting health data transformation for LandingPage');
    
    if (!azureFunctionResponse || !azureFunctionResponse.success || !azureFunctionResponse.data) {
      console.log('❌ INTEGRATION: No valid Azure Function data');
      return this.getEmptyHealthData();
    }
    
    // Use both regular data and fused data if available
    const rawData = azureFunctionResponse.data || [];
    const fusedData = azureFunctionResponse.fusedData;
    
    console.log(`📊 INTEGRATION: Processing ${rawData.length} data points`);
    
    // Group data by device type
    const deviceData = this.groupDataByDevice(rawData);
    console.log(`📱 INTEGRATION: Found devices: ${Object.keys(deviceData).join(', ')}`);
    
    // Extract comprehensive metrics
    const transformedData = {
      // Real-time metrics for dashboard tiles
      realTimeMetrics: {
        steps: this.extractStepsValue(deviceData, fusedData),
        heartRate: this.extractHeartRateValue(deviceData, fusedData),
        calories: this.extractCaloriesValue(deviceData, fusedData),
        sleep: this.extractSleepValue(deviceData, fusedData),
        water: this.extractWaterValue(deviceData, fusedData),
        stress: this.extractStressValue(deviceData, fusedData)
      },
      
      // Vitals section
      vitals: {
        temperature: this.extractTemperature(deviceData, fusedData),
        bloodPressure: this.extractBloodPressure(deviceData, fusedData),
        respiratoryRate: this.extractRespiratoryRate(deviceData, fusedData),
        bloodOxygen: this.extractBloodOxygen(deviceData, fusedData)
      },
      
      // Activity section
      activity: {
        steps: this.extractStepsDetail(deviceData, fusedData),
        calories: this.extractCaloriesDetail(deviceData, fusedData),
        distance: this.extractDistance(deviceData, fusedData),
        activeMinutes: this.extractActiveMinutes(deviceData, fusedData)
      },
      
      // Sleep section
      sleep: {
        duration: this.extractSleepDuration(deviceData, fusedData),
        quality: this.extractSleepQuality(deviceData, fusedData),
        score: this.extractSleepScore(deviceData, fusedData),
        stages: this.extractSleepStages(deviceData, fusedData)
      },
      
      // Wellness section
      wellness: {
        hydration: this.extractHydrationDetail(deviceData, fusedData),
        stress: this.extractStressDetail(deviceData, fusedData),
        readiness: this.extractReadiness(deviceData, fusedData),
        recovery: this.extractRecovery(deviceData, fusedData)
      },
      
      // Heart rate variability
      heartRateVariability: this.extractHRV(deviceData, fusedData),
      
      // Connected devices
      devices: this.extractConnectedDevices(deviceData),
      
      // Overall health score
      score: this.calculateHealthScore(deviceData, fusedData),
      
      // Data availability flags
      dataAvailability: this.calculateDataAvailability(deviceData),
      
      // Last sync information
      lastSync: this.getLastSyncTime(rawData),
      
      // Device status
      deviceStatus: this.getDeviceStatus(deviceData),
      
      // Placeholder for medications and active plans (will be merged by hook)
      medications: [],
      activePlans: [],
      alerts: [],
      recommendations: [],
      
      // Raw data for debugging
      _raw: {
        deviceData: deviceData,
        fusedData: fusedData,
        originalResponse: azureFunctionResponse
      }
    };
    
    console.log('✅ INTEGRATION: Health data transformation completed');
    console.log(`📊 METRICS: Steps=${transformedData.realTimeMetrics.steps}, HR=${transformedData.realTimeMetrics.heartRate}, Sleep=${transformedData.realTimeMetrics.sleep}`);
    
    return transformedData;
  },
  
  /**
   * Group raw data by device type
   */
  groupDataByDevice(rawData) {
    const grouped = {};
    
    rawData.forEach(dataPoint => {
      let deviceType = dataPoint._metadata?.deviceType;
      
      // Fallback device type detection
      if (!deviceType) {
        if (dataPoint.healthMetrics?.readiness) deviceType = 'oura-ring';
        else if (dataPoint.healthMetrics?.vitalSigns) deviceType = 'iphone-healthkit';
        else if (dataPoint.data?.metadata?.deviceInfo?.source === 'oura-ring') deviceType = 'oura-ring';
        else deviceType = 'unknown';
      }
      
      if (!grouped[deviceType]) {
        grouped[deviceType] = [];
      }
      grouped[deviceType].push(dataPoint);
    });
    
    return grouped;
  },
  
  /**
   * Extract steps value for dashboard tile
   */
  extractStepsValue(deviceData, fusedData) {
    // Priority: Fused data > Oura Ring > iPhone HealthKit
    if (fusedData?.fusedMetrics?.steps?.value) {
      return fusedData.fusedMetrics.steps.value;
    }
    
    // Oura Ring steps
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const steps = oura.healthMetrics?.activity?.steps;
      if (steps) return steps;
    }
    
    // iPhone HealthKit steps
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const steps = iphone.healthMetrics?.activity?.steps?.value;
      if (steps) return steps;
    }
    
    return 0;
  },
  
  /**
   * Extract heart rate value for dashboard tile
   */
  extractHeartRateValue(deviceData, fusedData) {
    // Priority: Fused data > Oura Ring > iPhone HealthKit
    if (fusedData?.fusedMetrics?.heartRate?.value) {
      return fusedData.fusedMetrics.heartRate.value;
    }
    
    // Oura Ring heart rate (resting)
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const hr = oura.healthMetrics?.heartRate?.resting || 
                oura.healthMetrics?.readiness?.resting_heart_rate;
      if (hr) return hr;
    }
    
    // iPhone HealthKit heart rate
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const hr = iphone.healthMetrics?.vitalSigns?.heartRate?.value;
      if (hr) return hr;
    }
    
    return null;
  },
  
  /**
   * Extract calories value for dashboard tile
   */
  extractCaloriesValue(deviceData, fusedData) {
    // Priority: Fused data > Oura Ring > iPhone HealthKit
    if (fusedData?.fusedMetrics?.calories?.value) {
      return fusedData.fusedMetrics.calories.value;
    }
    
    // Oura Ring calories
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const calories = oura.healthMetrics?.activity?.calories_total;
      if (calories) return calories;
    }
    
    // iPhone HealthKit calories
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const calories = iphone.healthMetrics?.activity?.caloriesBurned?.value;
      if (calories) return Math.round(calories);
    }
    
    return 0;
  },
  
  /**
   * Extract sleep value for dashboard tile
   */
  extractSleepValue(deviceData, fusedData) {
    // Priority: Fused data > Oura Ring > iPhone HealthKit
    if (fusedData?.fusedMetrics?.duration?.value) {
      return fusedData.fusedMetrics.duration.value;
    }
    
    // Oura Ring sleep (convert minutes to hours)
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const sleepMinutes = oura.healthMetrics?.sleep?.duration;
      if (sleepMinutes) return Math.round((sleepMinutes / 60) * 10) / 10;
    }
    
    // iPhone HealthKit sleep
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const sleepHours = iphone.healthMetrics?.lifestyle?.sleep?.value;
      if (sleepHours) return sleepHours;
    }
    
    return null;
  },
  
  /**
   * Extract water/hydration value for dashboard tile
   */
  extractWaterValue(deviceData, fusedData) {
    // Use fused data first if available (Apple-first priority)
    if (fusedData?.fusedMetrics?.waterIntake) {
      return fusedData.fusedMetrics.waterIntake.value;
    }
    
    // Apple Watch water intake (priority device)
    if (deviceData['apple-watch']?.length > 0) {
      const appleWatch = deviceData['apple-watch'][0];
      const waterMl = appleWatch.healthMetrics?.nutrition?.waterIntake?.value ||
                     appleWatch.healthMetrics?.hydration?.waterIntake?.value;
      if (waterMl) {
        // Convert ml to glasses (240ml per glass for Apple consistency)
        const glasses = Math.round(waterMl / 240);
        console.log(`💧 APPLE WATCH: ${waterMl}ml → ${glasses} glasses`);
        return glasses;
      }
    }
    
    // iPhone HealthKit water intake (secondary Apple device)
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const waterMl = iphone.healthMetrics?.lifestyle?.waterIntake?.value ||  // PRIMARY iPhone HealthKit path
                     iphone.healthMetrics?.lifestyle?.waterIntake ||          // Direct access
                     iphone.healthMetrics?.nutrition?.waterIntake?.value ||
                     iphone.healthMetrics?.hydration?.waterIntake?.value;
      if (waterMl) {
        // Convert ml to glasses (240ml per glass for Apple consistency)
        const glasses = Math.round(waterMl / 240);
        console.log(`💧 IPHONE: ${waterMl}ml → ${glasses} glasses`);
        return glasses;
      }
    }
    
    // Withings U-Scan (qualitative to quantitative conversion)
    if (deviceData['withings-uscan-nutrio']?.length > 0) {
      const withings = deviceData['withings-uscan-nutrio'][0];
      const hydrationLevel = withings.healthMetrics?.urinalysis?.hydrationLevel;
      if (hydrationLevel) {
        let glasses = 4; // Default
        switch (hydrationLevel) {
          case 'well_hydrated':
            glasses = 8;
            break;
          case 'adequately_hydrated':
            glasses = 6;
            break;
          case 'dehydrated':
            glasses = 3;
            break;
        }
        console.log(`💧 WITHINGS U-SCAN: ${hydrationLevel} → ${glasses} glasses`);
        return glasses;
      }
    }
    
    return 0;
  },
  
  /**
   * Extract stress value for dashboard tile
   */
  extractStressValue(deviceData, fusedData) {
    // Oura Ring stress level
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const stress = oura.healthMetrics?.stress;
      if (stress) {
        if (stress.level <= 30) return 'Low';
        if (stress.level <= 60) return 'Moderate';
        return 'High';
      }
    }
    
    return 'Unknown';
  },
  
  /**
   * Extract temperature data
   */
  extractTemperature(deviceData, fusedData) {
    // iPhone HealthKit temperature
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const temp = iphone.healthMetrics?.vitalSigns?.bodyTemperature;
      
      // FIXED: Exclude invalid temperature values that would show as fake data
      if (temp && temp.value && temp.value !== 0 && temp.value > 0) {
        // Additional validation: Ensure realistic temperature range
        const isValidCelsius = temp.value >= 30 && temp.value <= 45; // 30-45°C (86-113°F)
        const isValidFahrenheit = temp.value >= 86 && temp.value <= 113; // Direct Fahrenheit
        
        if (isValidCelsius || isValidFahrenheit) {
          // Convert Celsius to Fahrenheit if needed
          const tempF = temp.unit === 'celsius' ? (temp.value * 9/5) + 32 : temp.value;
          return {
            value: Math.round(tempF * 10) / 10,
            unit: '°F',
            timestamp: temp.timestamp,
            source: 'iphone-healthkit'
          };
        } else {
          console.log('🌡️ INVALID TEMPERATURE RANGE REJECTED in HealthDataIntegration:', temp.value, temp.unit);
        }
      } else {
        console.log('🌡️ TEMPERATURE REJECTED (null/0/invalid) in HealthDataIntegration:', temp?.value);
      }
    }
    
    // Oura Ring temperature
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const temp = oura.healthMetrics?.temperature?.body;
      if (temp) {
        return {
          value: temp,
          unit: '°F',
          timestamp: oura.timestamp,
          source: 'oura-ring'
        };
      }
    }
    
    return { value: null, unit: '°F', source: 'none' };
  },
  
  /**
   * Extract blood pressure data
   */
  extractBloodPressure(deviceData, fusedData) {
    // iPhone HealthKit blood pressure
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const bp = iphone.healthMetrics?.vitalSigns?.bloodPressure;
      if (bp && bp.systolic && bp.diastolic) {
        return {
          systolic: bp.systolic,
          diastolic: bp.diastolic,
          unit: bp.unit || 'mmHg',
          timestamp: bp.timestamp,
          source: 'iphone-healthkit'
        };
      }
    }
    
    return { systolic: null, diastolic: null, unit: 'mmHg', source: 'none' };
  },
  
  /**
   * Extract blood oxygen data
   */
  extractBloodOxygen(deviceData, fusedData) {
    // iPhone HealthKit blood oxygen
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const oxygen = iphone.healthMetrics?.vitalSigns?.bloodOxygen;
      if (oxygen) {
        return {
          value: oxygen.value,
          unit: oxygen.unit || '%',
          timestamp: oxygen.timestamp,
          source: 'iphone-healthkit'
        };
      }
    }
    
    return { value: null, unit: '%', source: 'none' };
  },
  
  /**
   * Extract respiratory rate data
   */
  extractRespiratoryRate(deviceData, fusedData) {
    // iPhone HealthKit respiratory rate
    if (deviceData['iphone-healthkit']?.length > 0) {
      const iphone = deviceData['iphone-healthkit'][0];
      const respRate = iphone.healthMetrics?.vitalSigns?.respiratoryRate;
      if (respRate) {
        return {
          value: respRate.value,
          unit: respRate.unit || 'breaths/min',
          timestamp: respRate.timestamp,
          source: 'iphone-healthkit'
        };
      }
    }
    
    return { value: null, unit: 'breaths/min', source: 'none' };
  },
  
  /**
   * Extract detailed steps data
   */
  extractStepsDetail(deviceData, fusedData) {
    const stepsValue = this.extractStepsValue(deviceData, fusedData);
    let source = 'none';
    
    if (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.activity?.steps) {
      source = 'oura-ring';
    } else if (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.activity?.steps) {
      source = 'iphone-healthkit';
    }
    
    return {
      value: stepsValue,
      goal: 10000,
      percentage: Math.min((stepsValue / 10000) * 100, 100),
      source: source
    };
  },
  
  /**
   * Extract detailed calories data
   */
  extractCaloriesDetail(deviceData, fusedData) {
    const caloriesValue = this.extractCaloriesValue(deviceData, fusedData);
    let source = 'none';
    
    if (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.activity?.calories_total) {
      source = 'oura-ring';
    } else if (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.activity?.caloriesBurned) {
      source = 'iphone-healthkit';
    }
    
    return {
      value: caloriesValue,
      goal: 2000,
      percentage: Math.min((caloriesValue / 2000) * 100, 100),
      source: source
    };
  },
  
  /**
   * Extract distance data
   */
  extractDistance(deviceData, fusedData) {
    // Oura Ring distance
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const distance = oura.healthMetrics?.activity?.equivalent_walking_distance;
      if (distance) {
        return {
          value: distance,
          unit: 'km',
          source: 'oura-ring'
        };
      }
    }
    
    return { value: 0, unit: 'km', source: 'none' };
  },
  
  /**
   * Extract active minutes data
   */
  extractActiveMinutes(deviceData, fusedData) {
    // Oura Ring active minutes
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const highActivity = oura.healthMetrics?.activity?.high_activity_met_minutes || 0;
      const mediumActivity = oura.healthMetrics?.activity?.medium_activity_met_minutes || 0;
      const totalActive = highActivity + mediumActivity;
      
      if (totalActive > 0) {
        return {
          value: totalActive,
          goal: 150, // WHO recommendation
          percentage: Math.min((totalActive / 150) * 100, 100),
          source: 'oura-ring'
        };
      }
    }
    
    return { value: 0, goal: 150, percentage: 0, source: 'none' };
  },
  
  /**
   * Extract sleep duration data
   */
  extractSleepDuration(deviceData, fusedData) {
    const sleepValue = this.extractSleepValue(deviceData, fusedData);
    let source = 'none';
    
    if (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.sleep?.duration) {
      source = 'oura-ring';
    } else if (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.lifestyle?.sleep) {
      source = 'iphone-healthkit';
    }
    
    return {
      value: sleepValue,
      goal: 8,
      percentage: Math.min((sleepValue / 8) * 100, 100),
      source: source
    };
  },
  
  /**
   * Extract sleep quality data
   */
  extractSleepQuality(deviceData, fusedData) {
    // Oura Ring sleep efficiency
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const efficiency = oura.healthMetrics?.sleep?.efficiency;
      if (efficiency) {
        return {
          value: efficiency,
          unit: '%',
          source: 'oura-ring'
        };
      }
    }
    
    return { value: null, unit: '%', source: 'none' };
  },
  
  /**
   * Extract sleep score data
   */
  extractSleepScore(deviceData, fusedData) {
    // Oura Ring sleep score
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const score = oura.healthMetrics?.sleep?.score;
      if (score) {
        return {
          value: score,
          source: 'oura-ring'
        };
      }
    }
    
    return { value: null, source: 'none' };
  },
  
  /**
   * Extract sleep stages data
   */
  extractSleepStages(deviceData, fusedData) {
    // Oura Ring sleep stages
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const stages = oura.healthMetrics?.sleep?.stages;
      if (stages) {
        return {
          light: stages.light,
          deep: stages.deep,
          rem: stages.rem,
          awake: stages.awake,
          source: 'oura-ring'
        };
      }
    }
    
    return { light: null, deep: null, rem: null, awake: null, source: 'none' };
  },
  
  /**
   * Extract detailed hydration data
   */
  extractHydrationDetail(deviceData, fusedData) {
    const waterValue = this.extractWaterValue(deviceData, fusedData);
    const waterMl = waterValue * 250; // Convert glasses to ml
    
    return {
      glasses: waterValue,
      ml: waterMl,
      target: 2000,
      percentage: Math.min((waterMl / 2000) * 100, 100),
      source: waterValue > 0 ? 'iphone-healthkit' : 'none'
    };
  },
  
  /**
   * Extract detailed stress data
   */
  extractStressDetail(deviceData, fusedData) {
    // Oura Ring stress data
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const stress = oura.healthMetrics?.stress;
      if (stress) {
        let level = 'Unknown';
        if (stress.level <= 30) level = 'Low';
        else if (stress.level <= 60) level = 'Moderate';
        else level = 'High';
        
        return {
          level: level,
          value: stress.level,
          resilience: stress.resilience,
          recoveryTime: stress.recovery_time,
          source: 'oura-ring'
        };
      }
    }
    
    return { level: 'Unknown', value: null, source: 'none' };
  },
  
  /**
   * Extract readiness data
   */
  extractReadiness(deviceData, fusedData) {
    // Oura Ring readiness
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const readiness = oura.healthMetrics?.readiness;
      if (readiness && readiness.score) {
        let level = 'Poor';
        if (readiness.score >= 85) level = 'Excellent';
        else if (readiness.score >= 70) level = 'Good';
        else if (readiness.score >= 55) level = 'Fair';
        
        return {
          score: readiness.score,
          level: level,
          recoveryIndex: readiness.recovery_index,
          temperatureDeviation: readiness.temperature_deviation,
          activityBalance: readiness.activity_balance,
          sleepBalance: readiness.sleep_balance,
          hrvBalance: readiness.hrv_balance,
          source: 'oura-ring'
        };
      }
    }
    
    return { score: null, level: 'Unknown', source: 'none' };
  },
  
  /**
   * Extract recovery data
   */
  extractRecovery(deviceData, fusedData) {
    // Use readiness as recovery metric for Oura Ring
    const readiness = this.extractReadiness(deviceData, fusedData);
    
    if (readiness.score) {
      return {
        score: readiness.recoveryIndex || readiness.score,
        level: readiness.level,
        source: readiness.source
      };
    }
    
    return { score: null, level: 'Unknown', source: 'none' };
  },
  
  /**
   * Extract heart rate variability data
   */
  extractHRV(deviceData, fusedData) {
    // Oura Ring HRV
    if (deviceData['oura-ring']?.length > 0) {
      const oura = deviceData['oura-ring'][0];
      const hrv = oura.healthMetrics?.heartRateVariability;
      if (hrv) {
        return {
          rmssd: hrv.rmssd,
          balance: hrv.balance,
          source: 'oura-ring'
        };
      }
    }
    
    return { rmssd: null, balance: null, source: 'none' };
  },
  
  /**
   * Extract connected devices
   */
  extractConnectedDevices(deviceData) {
    const devices = [];
    
    Object.keys(deviceData).forEach(deviceType => {
      if (deviceData[deviceType]?.length > 0) {
        const latestData = deviceData[deviceType][0];
        
        // Get timestamp from various possible locations
        const timestamp = latestData.metadata?.timestamp || 
                         latestData._metadata?.fileDate || 
                         latestData._metadata?.lastModified ||
                         latestData.timestamp;
        
        if (deviceType === 'oura-ring') {
          devices.push({
            name: 'Oura Ring',
            type: 'oura-ring',
            connected: true,
            model: 'Oura Ring Gen3/4',
            lastSync: timestamp,
            region: latestData._metadata?.region
          });
        } else if (deviceType === 'iphone-healthkit') {
          devices.push({
            name: 'iPhone HealthKit',
            type: 'iphone-healthkit', 
            connected: true,
            lastSync: timestamp,
            region: latestData._metadata?.region
          });
        } else if (deviceType === 'apple-watch') {
          devices.push({
            name: 'Apple Watch',
            type: 'apple-watch',
            connected: true,
            lastSync: timestamp,
            region: latestData._metadata?.region
          });
        } else if (deviceType === 'withings-scale' || deviceType === 'withings-body-plus' || deviceType === 'withings-body-scan') {
          devices.push({
            name: 'Withings Scale',
            type: deviceType,
            connected: true,
            model: deviceType.includes('body-scan') ? 'Body Scan' : 'Smart Scale',
            lastSync: timestamp,
            region: latestData._metadata?.region
          });
        } else {
          // Generic device mapping
          const deviceNames = {
            'garmin-watch': 'Garmin Watch',
            'fitbit-tracker': 'Fitbit Tracker', 
            'samsung-health': 'Samsung Health',
            'google-fit': 'Google Fit'
          };
          
          devices.push({
            name: deviceNames[deviceType] || deviceType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: deviceType,
            connected: true,
            lastSync: timestamp,
            region: latestData._metadata?.region
          });
        }
      }
    });
    
    console.log(`📱 DEVICES EXTRACTED: Found ${devices.length} connected devices:`, devices.map(d => d.name).join(', '));
    return devices;
  },
  
  /**
   * Calculate health score
   */
  calculateHealthScore(deviceData, fusedData) {
    let totalScore = 0;
    let factors = 0;
    const components = {};
    
    // Activity score
    const steps = this.extractStepsValue(deviceData, fusedData);
    if (steps > 0) {
      const activityScore = Math.min((steps / 10000) * 100, 100);
      components.activity = Math.round(activityScore);
      totalScore += activityScore;
      factors++;
    }
    
    // Sleep score
    const sleep = this.extractSleepValue(deviceData, fusedData);
    if (sleep) {
      // Check if Oura Ring has sleep score
      let sleepScore = 0;
      if (deviceData['oura-ring']?.length > 0) {
        sleepScore = deviceData['oura-ring'][0].healthMetrics?.sleep?.score || 0;
      }
      if (!sleepScore && sleep > 0) {
        sleepScore = Math.min((sleep / 8) * 100, 100);
      }
      components.sleep = Math.round(sleepScore);
      totalScore += sleepScore;
      factors++;
    }
    
    // Heart rate score
    const heartRate = this.extractHeartRateValue(deviceData, fusedData);
    if (heartRate) {
      const hrScore = heartRate <= 60 ? 100 : Math.max(60, 100 - (heartRate - 60));
      components.cardiovascular = Math.round(hrScore);
      totalScore += hrScore;
      factors++;
    }
    
    // Readiness score (Oura Ring specific)
    if (deviceData['oura-ring']?.length > 0) {
      const readiness = deviceData['oura-ring'][0].healthMetrics?.readiness?.score;
      if (readiness) {
        components.recovery = readiness;
        totalScore += readiness;
        factors++;
      }
    }
    
    const overall = factors > 0 ? Math.round(totalScore / factors) : 0;
    
    return {
      overall: overall,
      components: components,
      factors: factors
    };
  },
  
  /**
   * Calculate data availability flags
   */
  calculateDataAvailability(deviceData) {
    return {
      steps: this.hasDataForMetric(deviceData, 'steps'),
      heartRate: this.hasDataForMetric(deviceData, 'heartRate'),
      calories: this.hasDataForMetric(deviceData, 'calories'),
      sleep: this.hasDataForMetric(deviceData, 'sleep'),
      temperature: this.hasDataForMetric(deviceData, 'temperature'),
      water: this.hasDataForMetric(deviceData, 'water'),
      stress: this.hasDataForMetric(deviceData, 'stress')
    };
  },
  
  /**
   * Check if data is available for specific metric
   */
  hasDataForMetric(deviceData, metric) {
    switch (metric) {
      case 'steps':
        return (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.activity?.steps) ||
               (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.activity?.steps?.value);
      case 'heartRate':
        return (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.heartRate?.resting) ||
               (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.vitalSigns?.heartRate?.value);
      case 'sleep':
        return (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.sleep?.duration) ||
               (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.lifestyle?.sleep?.value);
      case 'temperature':
        return (deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.vitalSigns?.bodyTemperature?.value) ||
               (deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.temperature?.body);
      case 'water':
        return deviceData['iphone-healthkit']?.length > 0 && deviceData['iphone-healthkit'][0].healthMetrics?.lifestyle?.waterIntake?.value;
      case 'stress':
        return deviceData['oura-ring']?.length > 0 && deviceData['oura-ring'][0].healthMetrics?.stress?.level;
      default:
        return false;
    }
  },
  
  /**
   * Get last sync time
   */
  getLastSyncTime(rawData) {
    if (!rawData || rawData.length === 0) return null;
    
    let latestSync = null;
    rawData.forEach(data => {
      const timestamp = data.timestamp || data._metadata?.lastModified || data.metadata?.receivedAt;
      if (timestamp && (!latestSync || new Date(timestamp) > new Date(latestSync))) {
        latestSync = timestamp;
      }
    });
    
    return latestSync;
  },
  
  /**
   * Get device status
   */
  getDeviceStatus(deviceData) {
    const status = {
      connected: Object.keys(deviceData).length > 0,
      totalDevices: Object.keys(deviceData).length,
      deviceTypes: Object.keys(deviceData)
    };
    
    return status;
  },
  
  /**
   * Return empty health data structure
   */
  getEmptyHealthData() {
    return {
      realTimeMetrics: {
        steps: 0,
        heartRate: null,
        calories: 0,
        sleep: null,
        water: 0,
        stress: 'Unknown'
      },
      vitals: {
        temperature: { value: null, unit: '°F', source: 'none' },
        bloodPressure: { systolic: null, diastolic: null, unit: 'mmHg', source: 'none' },
        respiratoryRate: { value: null, unit: 'breaths/min', source: 'none' },
        bloodOxygen: { value: null, unit: '%', source: 'none' }
      },
      activity: {
        steps: { value: 0, goal: 10000, percentage: 0, source: 'none' }
      },
      sleep: {
        duration: null,
        quality: null,
        score: null
      },
      wellness: {
        hydration: { value: 0, target: 2000, percentage: 0, source: 'none' },
        stress: { level: 'Unknown', source: 'none' }
      },
      devices: [],
      score: { overall: 0, components: {}, factors: 0 },
      dataAvailability: {
        steps: false,
        heartRate: false,
        calories: false,
        sleep: false,
        temperature: false,
        water: false,
        stress: false
      },
      lastSync: null,
      deviceStatus: { connected: false, totalDevices: 0, deviceTypes: [] }
    };
  }
};

// Export for use in React components
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HealthDataIntegration;
} else if (typeof window !== 'undefined') {
  window.HealthDataIntegration = HealthDataIntegration;
}

console.log('✅ Health Data Integration layer loaded successfully');
